import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApolloProvider } from '@apollo/react-hooks';
import { ApolloClient } from 'apollo-boost';

import RecaptchaPage from './RecaptchaPage';
import { SHOW_RECAPTCHA } from './utils';
import WarningModal from './WarningModal';
import { useTranslation } from 'react-i18next';
import { ApolloErrorEvent, ApolloErrorType } from './types';
import { APOLLO_ERRORS_EVENT } from '@shared/api/apollo';

export interface ApolloProviderProps<TCache> {
	client: ApolloClient<TCache>;
	children: React.ReactNode | React.ReactNode[] | null;
}

export const GuardedApolloProvider: React.FC<ApolloProviderProps<any>> = ({ children, client }) => {
	const [recaptchaIsVisible, setRecaptchaIsVisible] = useState(false);
	const [apolloErrors, setApolloErrors] = useState<string>('');
	const [apolloErrorType, setApolloErrorType] = useState<ApolloErrorType>();
	const onRecaptchaEvent = useCallback(() => setRecaptchaIsVisible(true), []);

	const { t } = useTranslation('GuardedApolloProvider');

	const onApolloErrors = (e: ApolloErrorEvent) => {
		setApolloErrors(e.detail.message);

		if (e.detail.type) {
			setApolloErrorType(e.detail.type);
		}
	};

	const onCloseWarningPopup = useCallback(() => {
		setApolloErrors('');
	}, []);

	useEffect(() => {
		document.addEventListener(SHOW_RECAPTCHA, onRecaptchaEvent);
		document.addEventListener(APOLLO_ERRORS_EVENT, onApolloErrors);

		return () => {
			document.removeEventListener(SHOW_RECAPTCHA, onRecaptchaEvent);
			document.removeEventListener(APOLLO_ERRORS_EVENT, onApolloErrors);
		};
	});

	return (
		<ApolloProvider client={client}>
			{children}
			{recaptchaIsVisible && createPortal(<RecaptchaPage />, document.body)}

			<WarningModal
				title={t('Ops...')}
				content={t('Unfortunately, something went wrong. Please try again or contact with us')}
				isOpen={!!apolloErrors}
				errorMessage={t(apolloErrors)}
				buttonText={t('Close')}
				onButtonClick={onCloseWarningPopup}
				hideContentText={apolloErrorType === ApolloErrorType.PnrBlocked}
			/>
		</ApolloProvider>
	);
};
